import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiTool, FiShield, FiStar, FiMapPin, FiClock, FiCheck, FiArrowRight } = FiIcons;

const Home = () => {
  const services = [
    {
      icon: FiPhone,
      title: 'Mobile Phone Sales',
      description: 'Latest smartphones from all major brands including iPhone, Samsung, Google Pixel, and more.',
      features: ['New & Unlocked Phones', 'Competitive Prices', 'Warranty Included']
    },
    {
      icon: FiTool,
      title: 'Expert Repairs',
      description: 'Professional repair services for all mobile devices with genuine parts and quick turnaround.',
      features: ['Screen Replacement', 'Battery Replacement', 'Water Damage Repair']
    },
    {
      icon: FiShield,
      title: 'Accessories & Protection',
      description: 'Premium cases, screen protectors, chargers, and accessories to keep your device safe.',
      features: ['Premium Cases', 'Screen Protectors', 'Wireless Chargers']
    }
  ];

  const featuredProducts = [
    { src: 'https://i.ibb.co/pvrLJgbX/images-1.jpg', alt: 'iPhone Collection' },
    { src: 'https://i.ibb.co/bjHqKqXM/images-1.png', alt: 'Samsung Galaxy Series' },
    { src: 'https://i.ibb.co/N017X4y/images-2.jpg', alt: 'Google Pixel Phones' },
    { src: 'https://i.ibb.co/v6qJvQyT/images-3.jpg', alt: 'Phone Accessories' },
    { src: 'https://i.ibb.co/PvZM0d0y/images-4.jpg', alt: 'Protective Cases' },
    { src: 'https://i.ibb.co/chBsmZbt/images-5.jpg', alt: 'Wireless Chargers' }
  ];

  const whyChooseUs = [
    {
      icon: FiStar,
      title: 'Expert Technicians',
      description: 'Certified professionals with years of experience in mobile repair and sales.'
    },
    {
      icon: FiShield,
      title: 'Quality Guarantee',
      description: 'All our products and services come with comprehensive warranty and quality assurance.'
    },
    {
      icon: FiClock,
      title: 'Quick Service',
      description: 'Fast turnaround times for repairs and immediate availability of popular devices.'
    },
    {
      icon: FiCheck,
      title: 'Competitive Pricing',
      description: 'Best prices in Toronto with flexible payment options and regular promotions.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-red-50 to-gray-100 py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h1 className="text-4xl md:text-6xl font-bold text-gray-800 leading-tight">
                Your Trusted
                <span className="block primary-red">Mobile Partner</span>
                in Toronto
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Discover the latest smartphones, get expert repair services, and find premium accessories 
                at Global Mobilemart Inc. We've been serving Toronto with quality mobile solutions since our establishment.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/services"
                  className="bg-primary-red text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-700 transition-all duration-300 transform hover:scale-105 text-center"
                >
                  Explore Services
                </Link>
                <Link
                  to="/contact"
                  className="border-2 border-primary-red primary-red px-8 py-4 rounded-lg font-semibold hover-primary-red hover:text-white transition-all duration-300 text-center"
                >
                  Contact Us
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="grid grid-cols-2 gap-4"
            >
              {featuredProducts.slice(0, 4).map((product, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.05 }}
                  className="bg-white rounded-xl shadow-lg overflow-hidden"
                >
                  <img
                    src={product.src}
                    alt={product.alt}
                    className="w-full h-32 object-cover"
                  />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Premium Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From the latest smartphones to expert repair services, we provide comprehensive mobile solutions 
              with unmatched quality and customer service in Toronto.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-2"
              >
                <div className="bg-primary-red text-white w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <SafeIcon icon={service.icon} className="text-2xl" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-800 mb-4 text-center">{service.title}</h3>
                <p className="text-gray-600 mb-6 text-center leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <SafeIcon icon={FiCheck} className="text-green-500 mr-3 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Why Choose Global Mobilemart?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              With years of experience in the mobile industry, we've built a reputation for excellence, 
              reliability, and customer satisfaction in the Toronto community.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="bg-primary-red text-white w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <SafeIcon icon={item.icon} className="text-2xl" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Gallery */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Product Gallery</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our extensive collection of mobile phones, accessories, and repair services. 
              We stock the latest devices from all major manufacturers.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[
              'https://i.ibb.co/pvrLJgbX/images-1.jpg',
              'https://i.ibb.co/bjHqKqXM/images-1.png',
              'https://i.ibb.co/N017X4y/images-2.jpg',
              'https://i.ibb.co/v6qJvQyT/images-3.jpg',
              'https://i.ibb.co/PvZM0d0y/images-4.jpg',
              'https://i.ibb.co/chBsmZbt/images-5.jpg',
              'https://i.ibb.co/TD98NF4P/images-6.jpg',
              'https://i.ibb.co/7JvgXKyr/images-7.jpg',
              'https://i.ibb.co/35YjJ3Cj/images-8.jpg',
              'https://i.ibb.co/r2DZdBHH/images-9.jpg',
              'https://i.ibb.co/tM1ttkJS/images-11.jpg',
              'https://i.ibb.co/KchKWPTW/images-12.jpg'
            ].map((src, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
                className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer"
              >
                <img
                  src={src}
                  alt={`Product ${index + 1}`}
                  className="w-full h-48 object-cover hover:opacity-90 transition-opacity duration-300"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Location & Hours */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-bold text-red-400 mb-6">Visit Our Store</h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiMapPin} className="text-red-400 mt-1 flex-shrink-0 text-xl" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Our Location</h3>
                    <p className="text-gray-300 leading-relaxed">
                      1760 Eglinton Ave W, Toronto, ON M6E 2H6, Canada
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiClock} className="text-red-400 mt-1 flex-shrink-0 text-xl" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Store Hours</h3>
                    <div className="text-gray-300 space-y-1">
                      <p>Monday - Friday: 9:00 AM - 7:00 PM</p>
                      <p>Saturday: 10:00 AM - 6:00 PM</p>
                      <p>Sunday: Closed</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <SafeIcon icon={FiPhone} className="text-red-400 flex-shrink-0 text-xl" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Call Us</h3>
                    <p className="text-gray-300">+14374381760</p>
                  </div>
                </div>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-2 bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-all duration-300"
              >
                <span>Get Directions</span>
                <SafeIcon icon={FiArrowRight} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gray-800 rounded-xl overflow-hidden shadow-2xl"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2884.6870894221097!2d-79.4482563!3d43.6962683!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x882b33c17a2f1181%3A0xc5a6f0ff7a0208f8!2sGlobal%20Mobilemart%20Inc!5e0!3m2!1sen!2s!4v1765200921124!5m2!1sen!2s"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Global Mobilemart Inc Location"
                className="w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-red text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto space-y-6"
          >
            <h2 className="text-4xl font-bold mb-4">Ready to Experience Excellence?</h2>
            <p className="text-xl text-red-100 leading-relaxed">
              Join thousands of satisfied customers who trust Global Mobilemart Inc for all their mobile needs. 
              Visit our store today or contact us for expert advice and premium service.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
              <Link
                to="/contact"
                className="bg-white text-red-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105"
              >
                Visit Our Store
              </Link>
              <a
                href="tel:+14374381760"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-red-600 transition-all duration-300"
              >
                Call Now: +14374381760
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;